import React, { useMemo, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap"

// Custom Components
import TableContainer from "components/Common/TableContainer"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Hooks
import {
  useColors,
  useCreateColor,
  useEditColor,
  useDeleteColor,
} from "hooks/colorHook"
import { useListParams } from "hooks/useListParams"
import { formatReadableDate } from "utils/formatDate"

const ColorList = () => {
  document.title = "Color List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [color, setColor] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: colorsResponse,
    isLoading,
    refetch: colorRefetch,
    isRefetching: colorRefetchLoading,
  } = useColors(queryParams)

  const { mutateAsync: createColor, isPending: colorCreateLoading } =
    useCreateColor()
  const { mutateAsync: editColor, isPending: colorEditLoading } = useEditColor()
  const { mutateAsync: deleteColor, isPending: colorDeleteLoading } =
    useDeleteColor()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setColor(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setColor(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleColorClick = useCallback(
    arg => {
      setColor({ id: arg.id, name: arg.name })
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(colorData => {
    setColor(colorData)
    setDeleteModal(true)
  }, [])

  const handleDeleteColor = useCallback(async () => {
    if (color?.id) {
      try {
        const res = await deleteColor(color.id)
        toast.success(res?.message ?? "Color deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error("Failed to delete color:", error)
        toast.error("Failed to delete color.")
      }
    }
  }, [color, deleteColor])

  const handleColorListRefresh = () => {
    colorRefetch()
  }

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters long")
      .required("Please enter color name"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = { name: values.name }
      const action = isEdit
        ? editColor({ id: color.id, ...payload })
        : createColor(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Color ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggle()
      } catch (error) {
        console.error("Failed to save color:", error)
        toast.error("Failed to save color.")
      }
    },
    [isEdit, color, editColor, createColor, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { name: color?.name || "" },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Slug",
        accessorKey: "slug",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleColorClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleColorClick, onClickDelete]
  )

  const colorsData = colorsResponse?.data?.data || []
  const paginationInfo = colorsResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteColor}
        loading={colorDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Color" breadcrumbItem="Color Lists" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">Color Lists</h5>
                    <div className="flex-shrink-0">
                      <Button
                        color="primary"
                        onClick={handleAddNewClick}
                        className="me-1"
                      >
                        Add New Color
                      </Button>
                      <Button
                        color="light"
                        onClick={handleColorListRefresh}
                        className="me-1"
                        id="refreshTooltip"
                      >
                        <i
                          className={`mdi mdi-refresh ${
                            colorRefetchLoading ? "mdi-spin" : ""
                          }`}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="refreshTooltip"
                        >
                          Refresh
                        </UncontrolledTooltip>
                      </Button>
                      {hasActiveFilters && (
                        <Button
                          color="danger"
                          onClick={resetParams}
                          className="me-1"
                        >
                          <i className="mdi mdi-close" />
                          Reset Filters
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={colorsData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || colorRefetchLoading}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>
              {isEdit ? "Edit Color" : "Add Color"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Color Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter color name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  {validation.touched.name && validation.errors.name ? (
                    <FormFeedback type="invalid">
                      {validation.errors.name}
                    </FormFeedback>
                  ) : null}
                </div>
                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={colorEditLoading || colorCreateLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Color"}
                    {(colorEditLoading || colorCreateLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default ColorList
